import axios from "../../axios";
import { AxiosError } from "axios";
import { ProgramSeverityData } from "../../../hooks/dashboard/useSeverityAndTrend";

/**
 * Fetches unified business dashboard data from the API
 * 
 * This is the main method for retrieving all dashboard data in a single request,
 * which is more efficient than making multiple API calls.
 * 
 * The unified endpoint returns:
 * - Dashboard metrics (totalReports, activeIssues, resolvedIssues, retestsRemaining)
 * - Severity overview (total and per program)
 * - Reports trend data (weekly, monthly, quarterly, yearly)
 * - Business review reports (issues requiring business attention)
 * - Retest summary metrics (status counts, pass rates, etc.)
 * - Retest actions data (recent retest activities)
 * 
 * @returns Promise resolving to the unified dashboard data
 */
interface CacheEntry<T> {
  data: T;
  timestamp: number;
}

const dashboardApiCache = new Map<string, CacheEntry<any>>();
const inFlightRequests = new Map<string, Promise<any>>();
const DEFAULT_TTL_MS = 30_000; // 30 seconds

/**
 * Clears the in-memory dashboard API cache and pending requests
 */
export function clearDashboardApiCache(): void {
  dashboardApiCache.clear();
  inFlightRequests.clear();
}

/**
 * Synchronously retrieves cached data if still within TTL.
 */
export function getCachedDashboardData<T>(key: string, ttlMs: number = DEFAULT_TTL_MS): T | undefined {
  const cached = dashboardApiCache.get(key);
  if (cached && Date.now() - cached.timestamp < ttlMs) {
    return cached.data as T;
  }
  return undefined;
}

/**
 * Generic helper for dashboard requests that deduplicates identical concurrent requests
 * and caches results in memory for fast subsequent renders.
 */
export async function cachedDashboardRequest<T>(
  key: string,
  fetchFn: () => Promise<T>,
  ttlMs: number = DEFAULT_TTL_MS
): Promise<T> {
  const cached = dashboardApiCache.get(key);
  if (cached && Date.now() - cached.timestamp < ttlMs) {
    return cached.data;
  }

  const inFlight = inFlightRequests.get(key);
  if (inFlight) {
    return inFlight;
  }

  const promise = (async () => {
    try {
      const data = await fetchFn();
      dashboardApiCache.set(key, { data, timestamp: Date.now() });
      return data;
    } finally {
      inFlightRequests.delete(key);
    }
  })();

  inFlightRequests.set(key, promise);
  return promise;
}

export const getUnifiedBusinessDashboard = async (includeArchived?: boolean, subscriptionOnly?: boolean, selectedYear?: number | null) => {
  const cacheKey = `unified:${includeArchived ?? false}:${subscriptionOnly ?? false}:${selectedYear ?? ""}`;
  return cachedDashboardRequest(cacheKey, async () => {
    try {
      const response = await axios.get('/dashboard/businessDashboard', {
        params: {
          includeArchived,
          subscriptionOnly,
          selectedYear
        }
      });
      
      if (response.status !== 200) {
        throw new Error(`API returned status ${response.status}`);
      }
      
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      throw error;
    }
  });
};

// The following functions are kept for backward compatibility
// They now use the unified dashboard endpoint internally

/**
 * Safely extracts severity category data case-insensitively,
 * mapping informational/info/none/null categories all to 'info'.
 */
const getSeverityCategoryData = (severityData: any, targetCategory: string) => {
  if (!severityData) return undefined;
  
  const keys = Object.keys(severityData);
  for (const key of keys) {
    const lowerKey = key.toLowerCase().trim();
    
    // Match standard categories
    if (lowerKey === targetCategory) {
      return severityData[key];
    }
    
    // Match alternative info categories
    if (targetCategory === 'info') {
      if (
        lowerKey === 'informational' ||
        lowerKey === 'none' ||
        lowerKey === 'null' ||
        lowerKey === 'info'
      ) {
        return severityData[key];
      }
    }
  }
  return undefined;
};

/**
 * Fetches the dashboard summary metrics by extracting from the unified dashboard
 * @returns Dashboard summary data including total reports, active reports, resolved issues, and retests remaining
 */
export const getDashboardSummary = async (subscriptionOnly?: boolean, selectedYear?: number | null) => {
  try {
    const response = await getUnifiedBusinessDashboard(undefined, subscriptionOnly, selectedYear);
    
    if (!response?.success || !response?.data?.metrics) {
      throw new Error("Invalid dashboard data structure");
    }
    
    // Check if we have severityOverview data to calculate active reports
    if (response.data.severityOverview && response.data.severityOverview.total) {
      const severityData = response.data.severityOverview.total;
      
      // Calculate active reports from severity data in the same way as the donut chart:
      // Active = Total - (BusinessResolved + RealResolved)
      let activeIssuesCount = 0;
      let totalIssuesCount = 0;
      
      // Process each severity category
      ['critical', 'high', 'medium', 'low', 'info'].forEach(category => {
        const categoryData = getSeverityCategoryData(severityData, category);
        if (categoryData) {
          // Total count of issues
          totalIssuesCount += categoryData.total || 0;
          
          // Business accepted issues
          const businessResolved = categoryData.businessResolved || 0;
          
          // Truly resolved issues (fixed)
          const realResolved = categoryData.realResolved || 0;
          
          // Active reports = Total - (BusinessResolved + RealResolved)
          const activeInCategory = Math.max(0, (categoryData.total || 0) - businessResolved - realResolved);
          activeIssuesCount += activeInCategory;
        }
      });
      
      // Calculate a change percentage (can be null if no history data available)
      const changePercent = totalIssuesCount > 0 ? 
          Math.round((activeIssuesCount / totalIssuesCount) * 100) - 100 : null;
      
      // Add activeIssues to the response
      return {
        success: true,
        data: {
          ...response.data.metrics,
          activeIssues: {
            current: activeIssuesCount,
            changePercent: changePercent
          }
        }
      };
    }
    
    // If no severity data available, just return metrics as is
    return {
      success: true,
      data: response.data.metrics
    };
  } catch (error) {
    throw error;
  }
};

/**
 * Fetches severity overview and trend analysis data from unified dashboard
 * @returns Data for severity donut chart and reports trend line chart
 */
export const getSeverityAndTrend = async (subscriptionOnly?: boolean, selectedYear?: number | null) => {
  try {
    const response = await getUnifiedBusinessDashboard(undefined, subscriptionOnly, selectedYear);
    
    if (!response?.success || !response?.data?.severityOverview || !response?.data?.reportsTrend) {
      throw new Error("Invalid dashboard data structure");
    }
    
    return {
      success: true,
      data: {
        severityOverview: response.data.severityOverview,
        reportsTrend: response.data.reportsTrend,
        metadata: {
          dataStartDate: null,
          dataEndDate: null
        }
      }
    };
  } catch (error) {
    throw error;
  }
};

/**
 * Fetches severity overview data with optional filtering from unified dashboard
 * @param filter - Optional filter parameter ('all' or 'accepted-risk' = 'all')
 * @returns Filtered severity overview data for donut chart
 */
export const getFilteredSeverityOverview = async (filter: 'all' | 'accepted-risk' = 'all', subscriptionOnly?: boolean, selectedYear?: number | null) => {
  try {
    const response = await getUnifiedBusinessDashboard(undefined, subscriptionOnly, selectedYear);
    
    if (!response?.success || !response?.data?.severityOverview) {
      throw new Error("Invalid dashboard data structure");
    }
    
    // If we want the 'all' data, just return the data as is
    if (filter === 'all') {
      return {
        success: true,
        data: response.data.severityOverview,
        filter: filter
      };
    }
    
    // For 'accepted-risk' filter, we need to transform the data to include only business resolved items
    if (filter === 'accepted-risk') {
      const originalData = response.data.severityOverview;
      
      // Helper to safely get risk value for any category case-insensitively
      const getCategoryRisk = (data: any, category: string) => {
        const catData = getSeverityCategoryData(data, category);
        return catData ? {
          total: catData.businessResolved || 0,
          businessResolved: catData.businessResolved || 0,
          realResolved: 0
        } : { total: 0, businessResolved: 0, realResolved: 0 };
      };

      // Create a filtered version where:
      // 1. total = businessResolved (only show accepted risk items)
      // 2. businessResolved remains the same
      // 3. Set realResolved to 0 (we don't want to show these in accepted risk view)
      const filteredData = {
        total: {
          critical: getCategoryRisk(originalData.total, 'critical'),
          high: getCategoryRisk(originalData.total, 'high'),
          medium: getCategoryRisk(originalData.total, 'medium'),
          low: getCategoryRisk(originalData.total, 'low'),
          info: getCategoryRisk(originalData.total, 'info')
        },
        programs: originalData.programs.map((program: ProgramSeverityData) => ({
          ...program,
          severityOverview: {
            critical: getCategoryRisk(program.severityOverview, 'critical'),
            high: getCategoryRisk(program.severityOverview, 'high'),
            medium: getCategoryRisk(program.severityOverview, 'medium'),
            low: getCategoryRisk(program.severityOverview, 'low'),
            info: getCategoryRisk(program.severityOverview, 'info')
          }
        }))
      };
      
      return {
        success: true,
        data: filteredData,
        filter: filter
      };
    }
    
    return {
      success: true,
      data: response.data.severityOverview,
      filter: filter
    };
  } catch (error) {
    throw error;
  }
};

/**
 * Fetches business review issues from unified dashboard
 * @returns List of reports requiring business review and other high priority issues
 */
export const getBusinessReviewIssues = async () => {
  try {
    const response = await getUnifiedBusinessDashboard();
    
    if (!response?.success || !response?.data?.businessReviewReports) {
      throw new Error("Invalid dashboard data structure");
    }
    
    // Use totalReports from metrics if available for more accurate total count
    const totalCount = response.data?.metrics?.totalReports?.current || response.data.businessReviewReports.length;
    const hasMore = totalCount > response.data.businessReviewReports.length;
    
    return {
      success: true,
      data: response.data.businessReviewReports,
      meta: {
        hasMore,
        totalCount
      }
    };
  } catch (error) {
    throw error;
  }
};

/**
 * Fetches company quota and run coverage data
 * @returns Promise resolving to the company quota and comprehensive run coverage details
 */
export const getCompanyQuota = async () => {
  try {
    const response = await axios.get('/v2/program-runs/quota');
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Fetches all subscription periods for the current user's company
 * @returns Promise resolving to an array of subscription objects
 */
export const getMyCompanySubscriptions = async () => {
  try {
    const response = await axios.get('/v2/companies/my-company/subscriptions');
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Checks if the business has any assets
 * @returns Promise resolving to { success: boolean, data: { hasAssets: boolean } }
 */
export const checkHasAssets = async () => {
  try {
    const response = await axios.get('/dashboard/hasAssets');
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Fetches the security posture score and breakdown
 * @returns Promise resolving to the posture score data
 */
export const getSecurityPosture = async (subscriptionOnly?: boolean, selectedYear?: number | null) => {
  try {
    const response = await axios.get('/dashboard/securityPosture', {
      params: { subscriptionOnly, selectedYear }
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Fetches the severity distribution across all programs
 * @returns Promise resolving to the severity distribution data
 */
export const getNewSeverityDistribution = async (timeRange: string = 'yearly', subscriptionOnly?: boolean, selectedYear?: number | null) => {
  const cacheKey = `severityDist:${timeRange}:${subscriptionOnly ?? false}:${selectedYear ?? ""}`;
  return cachedDashboardRequest(cacheKey, async () => {
    try {
      const response = await axios.get(`/dashboard/severityDistribution`, {
        params: { timeRange, subscriptionOnly, selectedYear }
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  });
};

/**
 * Fetches the 6-month risk trend
 * @returns Promise resolving to the risk trend data
 */
export const getMonthlyRiskTrend = async (timeRange: string = 'yearly', subscriptionOnly?: boolean, selectedYear?: number | null) => {
  const cacheKey = `riskTrend:${timeRange}:${subscriptionOnly ?? false}:${selectedYear ?? ""}`;
  return cachedDashboardRequest(cacheKey, async () => {
    try {
      const response = await axios.get(`/dashboard/riskTrend`, {
        params: { timeRange, subscriptionOnly, selectedYear }
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  });
};

/**
 * Fetches active programs with target type information
 * @returns Promise resolving to the active programs data
 */
export const getActiveDashboardPrograms = async (subscriptionOnly?: boolean, selectedYear?: number | null) => {
  const cacheKey = `activePrograms:${subscriptionOnly ?? false}:${selectedYear ?? ""}`;
  return cachedDashboardRequest(cacheKey, async () => {
    try {
      const response = await axios.get('/dashboard/activePrograms', {
        params: { subscriptionOnly, selectedYear }
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  });
};

/**
 * Fetches the 6-month vulnerability trend (reported vs resolved)
 * @returns Promise resolving to the vulnerability trend data
 */
export const getVulnerabilityTrend = async (timeRange: string = 'yearly', subscriptionOnly?: boolean, selectedYear?: number | null) => {
  const cacheKey = `vulnTrend:${timeRange}:${subscriptionOnly ?? false}:${selectedYear ?? ""}`;
  return cachedDashboardRequest(cacheKey, async () => {
    try {
      const response = await axios.get(`/dashboard/vulnerabilityTrend`, {
        params: { timeRange, subscriptionOnly, selectedYear }
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  });
};

/**
 * Fetches retest dashboard metrics (Patch efficacy, remediation time, etc.)
 * @returns Promise resolving to the retest dashboard data
 */
export const getRetestDashboard = async (timeRange: string = 'yearly', subscriptionOnly?: boolean, selectedYear?: number | null) => {
  const cacheKey = `retestDashboard:${timeRange}:${subscriptionOnly ?? false}:${selectedYear ?? ""}`;
  return cachedDashboardRequest(cacheKey, async () => {
    try {
      const response = await axios.get(`/dashboard/retestDashboard`, {
        params: { timeRange, subscriptionOnly, selectedYear }
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  });
};

/**
 * Fetches findings counts grouped by severity and specific statuses
 * @returns Promise resolving to the findings by severity and status data
 */
export const getFindingsSeverityStatus = async (timeRange: string = 'yearly', subscriptionOnly?: boolean, selectedYear?: number | null) => {
  const cacheKey = `findingsSeverity:${timeRange}:${subscriptionOnly ?? false}:${selectedYear ?? ""}`;
  return cachedDashboardRequest(cacheKey, async () => {
    try {
      const response = await axios.get(`/dashboard/findingsSeverityStatus`, {
        params: { timeRange, subscriptionOnly, selectedYear }
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  });
};

/**
 * Fetches the 4 top metrics for the Asset Inventory tab
 * @returns Promise resolving to the asset inventory metrics
 */
export const getAssetInventoryMetrics = async (subscriptionOnly?: boolean, selectedYear?: number | null) => {
  const cacheKey = `assetMetrics:${subscriptionOnly ?? false}:${selectedYear ?? ""}`;
  return cachedDashboardRequest(cacheKey, async () => {
    try {
      const response = await axios.get('/dashboard/assetInventoryMetrics', {
        params: { subscriptionOnly, selectedYear }
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  });
};

/**
 * Fetches data for the 4 charts in the Asset Inventory tab
 * @returns Promise resolving to the chart data
 */
export const getAssetInventoryCharts = async (timeRange: string = 'yearly', subscriptionOnly?: boolean, selectedYear?: number | null) => {
  const cacheKey = `assetCharts:${timeRange}:${subscriptionOnly ?? false}:${selectedYear ?? ""}`;
  return cachedDashboardRequest(cacheKey, async () => {
    try {
      const response = await axios.get(`/dashboard/assetInventoryCharts`, {
        params: { timeRange, subscriptionOnly, selectedYear }
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  });
};

/**
 * Fetches data for the Asset Tracker table in the Asset Inventory tab
 * @returns Promise resolving to the asset tracker data array
 */
export const getAssetTrackerData = async (subscriptionOnly?: boolean, selectedYear?: number | null) => {
  const cacheKey = `assetTracker:${subscriptionOnly ?? false}:${selectedYear ?? ""}`;
  return cachedDashboardRequest(cacheKey, async () => {
    try {
      const response = await axios.get('/dashboard/assetTracker', {
        params: { subscriptionOnly, selectedYear }
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  });
};
